import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Calendar, AlertCircle, UserPlus, Mail, Check, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { notificationsInboxApi, type InboxNotification } from '@/api/notifications';
import { useSseSubscription } from '@/hooks/useSseSubscription';

const NOTIFICATIONS_BASE_URL =
  (import.meta.env.VITE_NOTIFICATIONS_URL as string | undefined) || 'http://localhost:8084';

// Safety-net fallback while SSE is connected — covers rare proxy stalls.
const FALLBACK_REFETCH_MS = 5 * 60 * 1000;

const NOTIFICATIONS_PAGE_SIZE = 20;

function formatTimeAgo(createdAt: string): string {
  try {
    const diff = Date.now() - new Date(createdAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'ayer';
    return `hace ${days} días`;
  } catch {
    return '';
  }
}

function getIcon(eventType: string) {
  switch (eventType) {
    case 'APPOINTMENT_CREATED':
    case 'APPOINTMENT_CONFIRMED':
    case 'APPOINTMENT_COMPLETED':
      return { Icon: Calendar, color: 'var(--terracotta)', bg: 'rgba(224,123,84,0.1)' };
    case 'APPOINTMENT_CANCELLED':
    case 'APPOINTMENT_NO_SHOW':
      return { Icon: AlertCircle, color: '#dc2626', bg: 'rgba(220,38,38,0.1)' };
    case 'CLIENT_CREATED':
      return { Icon: UserPlus, color: '#252E3E', bg: 'rgba(37,46,62,0.1)' };
    case 'MESSAGE_SENT':
    case 'RETENTION_FOLLOWUP':
      return { Icon: Mail, color: 'var(--dark-teal)', bg: 'rgba(36,62,61,0.08)' };
    default:
      return { Icon: Bell, color: 'var(--muted-fg)', bg: 'var(--muted-bg)' };
  }
}

export function Notifications() {
  const businessId = useBusinessId();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [page, setPage] = useState(0);

  // Paged: the inbox accumulates for the life of the business and is never pruned.
  const { data: notificationPage, isLoading } = useQuery({
    queryKey: ['notifications', businessId, 'page', page],
    queryFn: () => notificationsInboxApi.listPaged(businessId, page, NOTIFICATIONS_PAGE_SIZE),
    enabled: !!businessId,
    refetchInterval: FALLBACK_REFETCH_MS,
    placeholderData: previous => previous,
  });

  const notifications = notificationPage?.content ?? [];
  const totalNotifications = notificationPage?.totalElements ?? 0;
  const pageCount = Math.max(1, notificationPage?.totalPages ?? 1);

  // Counts the whole inbox, not just this page, so it comes from the dedicated endpoint.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread', businessId],
    queryFn: () => notificationsInboxApi.unreadCount(businessId),
    enabled: !!businessId,
    refetchInterval: FALLBACK_REFETCH_MS,
  });

  const refreshNotifications = useCallback(() => {
    if (!businessId) return;
    void queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
    void queryClient.invalidateQueries({ queryKey: ['notifications-unread', businessId] });
  }, [queryClient, businessId]);

  useSseSubscription({
    url: businessId
      ? `${NOTIFICATIONS_BASE_URL}/api/v1/businesses/${businessId}/notifications/stream`
      : null,
    onEvent: refreshNotifications,
    onOpen: refreshNotifications,
    deps: [businessId],
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: number) => notificationsInboxApi.markRead(businessId, notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread', businessId] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsInboxApi.markAllRead(businessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread', businessId] });
    },
  });

  const selected = selectedId != null ? notifications.find(n => n.id === selectedId) : null;

  const handleSelect = (n: InboxNotification) => {
    setSelectedId(n.id);
    if (!n.read) {
      markReadMutation.mutate(n.id);
    }
  };

  // Detail view
  if (selected) {
    const { Icon, color, bg } = getIcon(selected.eventType);
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex-1 overflow-auto" style={{ paddingTop: '16px', paddingLeft: '14px', paddingRight: '14px', paddingBottom: '96px' }}>
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center gap-1 mb-4"
            style={{ fontSize: '14px', color: 'var(--terracotta)', cursor: 'pointer', background: 'none', border: 'none', marginLeft: '-6px' }}
          >
            <ChevronLeft size={18} />
            Notificaciones
          </button>

          <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '20px' }}>
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '40px', height: '40px', backgroundColor: bg }}>
                <Icon size={20} style={{ color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--dark-teal)', marginBottom: '4px' }}>
                  {selected.title}
                </h2>
                <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                  {formatTimeAgo(selected.createdAt)}
                </span>
              </div>
            </div>
            <p style={{ fontSize: '14px', color: 'var(--dark-teal)', lineHeight: 1.6 }}>
              {selected.body}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto" style={{ paddingTop: '16px', paddingLeft: '14px', paddingRight: '14px', paddingBottom: '96px' }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-baseline gap-2">
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>
              Notificaciones
            </span>
            {unreadCount > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                {unreadCount} sin leer
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="flex items-center gap-1 transition-colors"
              style={{ fontSize: '13px', color: 'var(--terracotta)', cursor: 'pointer', background: 'none', border: 'none' }}
            >
              <Check size={14} />
              Marcar todas como leídas
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin" style={{ color: 'var(--muted-fg)' }} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '40px 20px' }}>
            <Bell size={32} style={{ color: 'var(--border-color)', marginBottom: '12px' }} />
            <p style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>Sin notificaciones</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(n => {
              const { Icon, color, bg } = getIcon(n.eventType);
              return (
                <button
                  key={n.id}
                  onClick={() => handleSelect(n)}
                  className="w-full text-left rounded-xl transition-colors hover:shadow-sm"
                  style={{
                    backgroundColor: n.read ? 'var(--off-white)' : 'white',
                    border: `1px solid ${n.read ? 'var(--border-color)' : 'rgba(224,123,84,0.2)'}`,
                    padding: '14px 16px',
                    cursor: 'pointer',
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '36px', height: '36px', backgroundColor: bg }}>
                      <Icon size={18} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="truncate min-w-0" style={{ fontSize: '14px', fontWeight: n.read ? 400 : 600, color: 'var(--dark-teal)' }}>
                          {n.title}
                        </span>
                        {!n.read && (
                          <div className="flex-shrink-0 rounded-full" style={{ width: '8px', height: '8px', backgroundColor: 'var(--terracotta)' }} />
                        )}
                      </div>
                      <p className="truncate" style={{ fontSize: '13px', color: 'var(--muted-fg)', lineHeight: 1.4 }}>
                        {n.body}
                      </p>
                      <span style={{ fontSize: '11px', color: 'var(--muted-fg)', opacity: 0.7, marginTop: '4px', display: 'block' }}>
                        {formatTimeAgo(n.createdAt)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}

            {totalNotifications > NOTIFICATIONS_PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2">
                <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                  {page * NOTIFICATIONS_PAGE_SIZE + 1}–
                  {Math.min((page + 1) * NOTIFICATIONS_PAGE_SIZE, totalNotifications)} de {totalNotifications}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    aria-label="Página anterior"
                    className="rounded-lg flex items-center justify-center disabled:opacity-30"
                    style={{
                      width: '36px', height: '36px',
                      border: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)',
                      color: 'var(--dark-teal)',
                    }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                    disabled={page >= pageCount - 1}
                    aria-label="Página siguiente"
                    className="rounded-lg flex items-center justify-center disabled:opacity-30"
                    style={{
                      width: '36px', height: '36px',
                      border: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)',
                      color: 'var(--dark-teal)',
                    }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
